import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useActivityTracking } from '../../hooks/useActivityTracking';

// Route → page title. Central so every page gets a title in the Header
// without needing to touch every page component. Dynamic segments (/:id)
// are resolved below via useMatch.
const TITLES = {
  '/':                        'Dashboard',
  '/search':                  'Search orders',
  '/intake':                  'Intake batches',
  '/intake/upload':           'Upload MBO',
  '/dispatch':                'Dispatch batches',
  '/dispatch/ready':          'Ready for dispatch',
  '/scheduled-splits':        'Scheduled splits',
  '/pending-tracking':        'Pending orders',
  '/hold-orders':             'Hold orders',
  '/tracking/upload':         'Upload tracking',
  '/tracking/share-with-ops': 'Share with Ops',
  '/tracking/mbo-export':     'MBO export',
  '/tracking/carrier':        'Carrier tracking',
  '/my-queue':                'My queue',
  '/ops':                     'My queue',
  '/ops/all':                 'All orders',
  '/admin/data-quality':      'Data quality',
  '/admin/status-report':     'Status report',
  '/admin/product-mapping':   'Product mapping',
  '/admin/shipper-rules':     'Shipper rules',
  '/admin/validation-dashboard': 'Validation dashboard',
  '/admin/vendors':           'Vendors',
  '/admin/users':             'Users',
  '/admin/activity-logs':     'Activity log',
  '/settings':                'Settings',
};

function useRouteTitle() {
  const { pathname } = useLocation();
  // Rules of hooks: call all useMatch unconditionally on every render so
  // the hook count stays stable across navigations. Early-returning between
  // them changed the hook count per route → next useEffect crashed with
  // "Cannot read properties of undefined (reading 'length')" from React's
  // deps-array comparison.
  const intakeMatch   = useMatch('/intake/:id');
  const dispatchMatch = useMatch('/dispatch/:id');
  const orderMatch    = useMatch('/orders/:id');
  if (intakeMatch)   return 'Intake batch';
  if (dispatchMatch) return 'Dispatch batch';
  if (orderMatch)    return 'Order detail';
  return TITLES[pathname] || '';
}

export default function Layout() {
  // Emits page_view to /api/activity/events on every route change.
  useActivityTracking();
  const title = useRouteTitle();
  const { pathname } = useLocation();
  const mainRef = useRef(null);

  // Reset main's scrollTop on every navigation. Because <main> has its own
  // overflow-y-auto (not the window), the browser doesn't auto-reset scroll
  // on route change — the list page's scrollTop carried into detail pages
  // and made them look blank until a hard refresh.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  return (
    // h-screen + overflow-hidden on the wrapper so the sidebar is a fixed
    // column and ONLY <main> scrolls. Previous min-h-screen setup let body
    // scroll too, which broke the sticky header and made short pages look
    // blank when the sidebar pushed the flex row taller than the viewport.
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <main ref={mainRef} className="flex-1 overflow-y-auto">
        {/* Global top bar — page title + notification bell on every page */}
        <Header title={title} />
        <Outlet />
      </main>
    </div>
  );
}
